import type { ReactNode } from 'react';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { api } from '../api/client';
import BackgroundChip from './BackgroundChip';
import { SubTabContext, SubTabScenarioContext } from './engine/SubTabContext';
import ExplainerToggle from './engine/ExplainerToggle';
import WhyThisExists from './WhyThisExists';
import ExplainThis from './ExplainThis';
import type { RecommendedAction, AlertItem } from '../types';
import { CodeText } from './Code';
import HeadlineActionBand from './engine/HeadlineActionBand';
import ProvenanceChip from './ProvenanceChip';

export type SubTabTier = 'lighthouse' | 'flagship' | 'core';
export type SubTabMoat = 'core' | 'commodity';

export interface SubTabDef {
  id: string;
  name: string;
  tier: SubTabTier;
  enabled: boolean;
  moat?: SubTabMoat;
  render: () => ReactNode;
}

export interface BackgroundDef {
  id: string;
  name: string;
  status?: 'ok' | 'amber' | 'red';
}

interface EngineShellProps {
  engineId: number;
  engineName: string;
  engineCode: string;       // "E1", "E2", etc.
  subTabs: SubTabDef[];
  backgrounds?: BackgroundDef[];
  // Optional override for summary; if absent, fetches /api/engines/{id}/summary
  summaryOverride?: ReactNode;
}

// Tier drives only the default-tab ordering now (lighthouse first); the tier
// labels themselves are not shown — they crowded without adding value.
const TIER_ORDER: SubTabTier[] = ['lighthouse', 'flagship', 'core'];

export default function EngineShell({
  engineId,
  engineName,
  subTabs,
  backgrounds = [],
  summaryOverride,
}: EngineShellProps) {
  // §15.2: a deep link from an alert names the sub-tab + scenario + alert id.
  const [searchParams] = useSearchParams();
  const deepTab = searchParams.get('tab');
  const scenario = searchParams.get('scenario');
  const alertId = searchParams.get('alert');

  // First enabled sub-tab is the default; a deep-linked ?tab wins.
  const firstEnabled = subTabs.find((t) => t.enabled);
  const deepValid = deepTab && subTabs.some((t) => t.id === deepTab && t.enabled) ? deepTab : null;
  const [activeId, setActiveId] = useState<string>(deepValid ?? firstEnabled?.id ?? subTabs[0]?.id ?? '');

  // The alert that sent us here (for the context banner), if any.
  const alerts = useQuery({ queryKey: ['alerts'], queryFn: api.alerts, enabled: !!alertId, staleTime: 30_000 });
  const arrivedFrom: AlertItem | undefined = alertId ? (alerts.data ?? []).find((a) => a.id === alertId) : undefined;
  // Pre-run only applies to the deep-linked tab.
  const activeScenario = deepValid && activeId === deepValid ? scenario : null;

  const summary = useQuery({
    queryKey: ['engine', engineId, 'summary'],
    queryFn: async () => {
      const res = await fetch(`/api/engines/${engineId}/summary`, {
        credentials: 'include',
        headers: { 'x-demo-passcode': import.meta.env.VITE_DEMO_PASSCODE ?? 'arizona-c2-demo' },
      });
      if (!res.ok) return null;
      return res.json() as Promise<{
        engine_id: number;
        owner?: string;
        kpis?: Array<{ label: string; value: string; color?: string }>;
        binding_constraint?: string;
        background_chips?: Array<{ name: string; status?: 'ok' | 'amber' | 'red' }>;
        headline_plain?: string;
        recommended_action?: RecommendedAction | null;
      }>;
    },
    retry: 0,
  });

  // Group sub-tabs by tier in canonical order
  const grouped: Array<{ tier: SubTabTier; tabs: SubTabDef[] }> = TIER_ORDER
    .map((tier) => ({ tier, tabs: subTabs.filter((t) => t.tier === tier) }))
    .filter((g) => g.tabs.length > 0);

  const activeTab = subTabs.find((t) => t.id === activeId);
  const summaryBackgrounds = summary.data?.background_chips ?? backgrounds;

  return (
    <div>
      {/* Engine header */}
      <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
        <div className="ph">{engineId >= 6 ? 'Cross-cutting engine' : 'Operating engine'}</div>
        <ExplainerToggle view={activeId} engineId={engineId} />
      </div>

      {summaryOverride ?? (
        <div className="panel" style={{ marginBottom: 14 }}>
          <div className="row" style={{ justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
            <div>
              <div className="h2" style={{ fontSize: 24, marginBottom: 4 }}>{engineId} · {engineName.split(' · ')[1] ?? engineName}</div>
              {summary.data?.owner && (
                <div className="small">Owner: <b>{summary.data.owner}</b></div>
              )}
              {summary.data?.binding_constraint && (
                <div className="small" style={{ marginTop: 4 }}>
                  Binding: <span className="mono" style={{ color: 'var(--red)' }}>{summary.data.binding_constraint}</span>
                </div>
              )}
            </div>
            {summaryBackgrounds.length > 0 && (
              <div className="row" style={{ gap: 6, flexWrap: 'wrap', maxWidth: 360, justifyContent: 'flex-end' }}>
                {summaryBackgrounds.map((b, i) => (
                  <BackgroundChip key={i} name={b.name} status={b.status} />
                ))}
              </div>
            )}
          </div>
          {(summary.data?.kpis ?? []).length > 0 && (
            <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(summary.data!.kpis!.length, 4)}, 1fr)`, gap: 12, marginTop: 12 }}>
              {summary.data!.kpis!.map((k, i) => (
                <div key={i} style={{ padding: '10px 12px', background: 'var(--panel2)', border: '1px solid var(--line)', borderRadius: 8 }}>
                  <div className="small faint" style={{ textTransform: 'uppercase', letterSpacing: '0.06em', fontSize: 11, marginBottom: 4 }}>{k.label}</div>
                  <div className="mono" style={{ fontSize: 20, fontWeight: 700, color: k.color === 'red' ? 'var(--red)' : k.color === 'amber' ? 'var(--gold)' : k.color === 'olive' ? 'var(--olive)' : 'var(--ink)' }}>
                    {k.value}
                  </div>
                  <div style={{ marginTop: 6 }}><ProvenanceChip coverage="partial" /></div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Always-on, deterministic 'Why this exists' for the engine (spec 11.5, no model). */}
      <WhyThisExists engineId={engineId} />

      {/* Engine-level plain-language headline + rule-bound action (§12.2) */}
      {summary.data?.headline_plain && (
        <HeadlineActionBand
          view={`engine:${engineId}`}
          state={{
            engine_id: engineId,
            owner: summary.data.owner,
            kpis: summary.data.kpis,
            binding_constraint: summary.data.binding_constraint,
          }}
          headline={summary.data.headline_plain}
          action={summary.data.recommended_action ?? null}
          readOnlyAction={engineId === 6}
        />
      )}

      {/* 'Explain this in plain language' on the engine brief (spec 11.5; marked AI aid, the brief stays of record). */}
      {summary.data && (
        <ExplainThis
          engine_id={engineId}
          recommendation={summary.data.recommended_action?.text ?? summary.data.headline_plain}
          evidence={(summary.data.kpis ?? []).map((k) => ({ label: k.label, value: String(k.value), provenance: 'modeled estimate' }))}
          constraints={summary.data.binding_constraint ? [summary.data.binding_constraint] : []}
        />
      )}

      {/* Sub-tab strip — one clean row (no tier labels), ordered for sensible default */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginBottom: 14, padding: '12px 14px', background: 'var(--panel)', border: '1px solid var(--line)', borderRadius: 10 }}>
        {grouped.flatMap((g) => g.tabs).map((t) => {
          const isActive = t.id === activeId;
          const disabled = !t.enabled;
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => t.enabled && setActiveId(t.id)}
              disabled={disabled}
              style={{
                padding: '8px 14px',
                fontSize: 13,
                border: '1px solid',
                borderColor: isActive ? 'var(--amber)' : 'var(--line)',
                background: isActive ? 'var(--amber)' : disabled ? 'transparent' : 'var(--panel2)',
                color: isActive ? 'var(--on-amber)' : disabled ? 'var(--faint)' : 'var(--ink)',
                borderRadius: 6,
                cursor: disabled ? 'not-allowed' : 'pointer',
                fontWeight: isActive ? 600 : 500,
                transition: 'border-color 0.15s ease, background 0.15s ease',
                opacity: disabled ? 0.55 : 1,
              }}
              title={t.name}
            >
              {t.name}
            </button>
          );
        })}
      </div>

      {/* Context banner — you arrived here from an alert (§15.2) */}
      {arrivedFrom && (
        <div className="panel" style={{ marginBottom: 12, padding: '10px 14px', borderLeft: '3px solid var(--amber)', background: 'rgba(214,162,58,0.08)' }}>
          <div className="row" style={{ gap: 8, alignItems: 'baseline', flexWrap: 'wrap' }}>
            <span className="tag amber" style={{ fontSize: 10 }}>WHY YOU'RE HERE</span>
            <span style={{ fontSize: 14, color: 'var(--ink)' }}>{arrivedFrom.headline_plain}</span>
          </div>
          {arrivedFrom.recommended_action && (
            <div className="small" style={{ marginTop: 6 }}>
              <b style={{ color: 'var(--amber)' }}>Recommended:</b> <CodeText text={arrivedFrom.recommended_action.text} compact />
              {scenario && <span className="mono faint" style={{ marginLeft: 8, fontSize: 11 }}>scenario · {scenario} (pre-run)</span>}
            </div>
          )}
        </div>
      )}

      {/* Selected sub-tab content */}
      <div>
        {activeTab ? (
          <SubTabContext.Provider value={activeTab.id}>
            <SubTabScenarioContext.Provider value={activeScenario}>
              <WhyThisExists engineId={engineId} interventionId={activeTab.id} />
              {activeTab.render()}
            </SubTabScenarioContext.Provider>
          </SubTabContext.Provider>
        ) : (
          <div className="small faint">No tab selected.</div>
        )}
      </div>
    </div>
  );
}
